import { ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';

import { AuthService } from '../../../core/auth/auth.service';
import { OrganizacionesService } from '../../../core/auth/organizaciones.service';
import { OrganizacionDelUsuario } from '../../../core/services/graphql.service';

/**
 * Selector de organización: cambiar de cliente sin cerrar la sesión.
 *
 * Con una sola URL para varios clientes, la etiqueta del cliente activo es además el único
 * aviso de estar en el equivocado: sin ella, la única forma de darse cuenta es reconocer
 * los datos, que es tarde. Por eso, con una sola organización, se pinta la etiqueta sola,
 * igual que antes; el desplegable aparece cuando hay otra a la que ir (o cuando la lista no
 * se ha podido cargar, para ofrecer el selector de Auth0).
 *
 * El cambio lo hace `AuthService.cambiarDeOrganizacion`: un login nuevo contra Auth0 con la
 * organización elegida, que reutiliza la sesión SSO y no pide credenciales.
 */
@Component({
  selector: 'sg-selector-organizacion',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './selector-organizacion.component.html',
  styleUrls: ['./selector-organizacion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.sg-org--ajustes]': "variante === 'ajustes'" },
})
export class SelectorOrganizacionComponent implements OnInit {
  /**
   * - `barra`: bajo el nombre de la app, en la cabecera de la barra lateral. El menú se
   *   ancla a esa cabecera (que tiene que ser `position: relative`), no a la etiqueta.
   * - `ajustes`: botón «Cambiar» de la fila Organización del perfil, con el menú hacia
   *   arriba (la tarjeta recorta lo que sobresale por abajo); sin otra organización no pinta
   *   nada (la fila ya dice cuál es la actual).
   */
  @Input() variante: 'barra' | 'ajustes' = 'barra';

  protected readonly org = inject(OrganizacionesService);
  private readonly auth = inject(AuthService);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  protected readonly tenant = toSignal(this.auth.currentTenantId$, { initialValue: this.auth.currentTenantId });
  protected readonly abierto = signal(false);
  protected readonly seOfrece = computed(() => this.org.hayOtras() || this.org.estado() === 'error');

  private readonly disparador = viewChild<ElementRef<HTMLButtonElement>>('disparador');

  ngOnInit(): void {
    this.org.cargar();
  }

  protected alternar(evento: MouseEvent): void {
    evento.stopPropagation();
    if (this.abierto()) {
      this.cerrar();
      return;
    }
    this.abierto.set(true);
    // Al abrir, el foco a la primera opción: con teclado se elige sin salir del menú.
    setTimeout(() => this.opciones()[0]?.focus());
  }

  protected elegir(org: OrganizacionDelUsuario): void {
    // El menú se queda abierto con la rueda en la elegida hasta que sale la redirección.
    this.org.cambiarA(org);
  }

  protected elegirEnAuth0(): void {
    this.org.elegirEnAuth0();
  }

  protected reintentar(): void {
    this.org.reintentar();
  }

  /** Flechas, Inicio y Fin recorren las opciones; Tab sale del menú y lo cierra. */
  protected teclado(evento: KeyboardEvent): void {
    const opciones = this.opciones();
    if (!opciones.length) return;
    const actual = opciones.indexOf(document.activeElement as HTMLButtonElement);
    let siguiente: number | null = null;
    switch (evento.key) {
      case 'ArrowDown': siguiente = (actual + 1) % opciones.length; break;
      case 'ArrowUp': siguiente = (actual - 1 + opciones.length) % opciones.length; break;
      case 'Home': siguiente = 0; break;
      case 'End': siguiente = opciones.length - 1; break;
      case 'Tab': this.abierto.set(false); return;
    }
    if (siguiente !== null) {
      evento.preventDefault();
      opciones[siguiente].focus();
    }
  }

  @HostListener('document:click', ['$event'])
  protected alPulsarFuera(evento: MouseEvent): void {
    if (this.abierto() && !this.host.nativeElement.contains(evento.target as Node)) {
      this.abierto.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected alEscape(): void {
    if (this.abierto()) this.cerrar();
  }

  private cerrar(): void {
    this.abierto.set(false);
    this.disparador()?.nativeElement.focus();
  }

  /** Las opciones que se pueden elegir ahora (la actual y las bloqueadas no cuentan). */
  private opciones(): HTMLButtonElement[] {
    return Array.from(
      this.host.nativeElement.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)'),
    );
  }
}
